import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '@godaddy/react-mintl';
import classNames from 'classnames';
import useClickAway from 'react-use/lib/useClickAway';
import Link from '@ux/link';
import Spinner from '@ux/spinner';

import { useUserData, useRootProps } from '../UserDataProvider';
import { isModalOpen } from '../../lib/useModalOpen';
import useGetSsoLinks from '../../lib/useGetSsoLinks';
import AccountInfoFooter from './AccountInfoFooter';
import SignIn from './SignIn';
import CreateAccount from './CreateAccount';
import QuickLinks from './QuickLinks';
import InboxLinks from './InboxLinks';
import NavigationLink from '../NavigationLink';
import ProfileAvatar from '../ProfileAvatar';

import './AccountInfo.scss';
import { TYPES } from '../../lib/types';

export function AccountTrayNav({
  appEid,
  navigation,
  onClose,
  signOutHref,
  loginLink,
  userName,
  sidebarNavLinkHandler,
  accountTrayBottomNav,
  isReseller
}) {
  let accountTrayBottom = [];
  let accountTrayBottomContent = false;
  const { userData, userDataLoaded } = useUserData();
  const { loggedIn } = userDataLoaded && userData;


  if (Array.isArray(accountTrayBottomNav)) {
    accountTrayBottom = accountTrayBottomNav.map(link => {
      if (link.caption && link.href) {
        accountTrayBottomContent = true;
        return <NavigationLink
          link={ link }
          className={ classNames('customer-link-component') }
          onClose={ onClose }
          key={ link.caption }
          sidebarNavLinkHandler={ sidebarNavLinkHandler }
        >
          <span className='nav-link-label'>{ link.caption }</span>
        </NavigationLink>;
      }
      return '';
    });
  }

  if (React.isValidElement(accountTrayBottomNav)) {
    accountTrayBottom = accountTrayBottomNav;
    accountTrayBottomContent = true;
  }

  const accountTrayContent = (
    <><div className='account-info-divider' /><div className='account-tray-bottom-container'>{ accountTrayBottom }</div></>
  );

  return (
    <React.Fragment>
      {
        navigation.map((link, idx) => {

          if (link.caption === 'divider') {
            // eslint-disable-next-line react/no-array-index-key
            return <div className='account-info-divider' key={ `divider-${idx}` } />;
          }

          if (link.href.includes('{{username}}')) {
            link.href = link.href.replace('{{username}}', userName);
          }

          return (
            <>
              <div className={ classNames({
                'account-info-divider': link.caption === 'Edit Profile' || link.key === 'edit_profile_label'
              }) } />
              <NavigationLink
                link={ link }
                className={ classNames('customer-link-component', {
                  'edit-profile-link': link.caption === 'Edit Profile' || link.key === 'edit_profile_label'
                }) }
                onClose={ onClose }
                key={ link.caption }
                sidebarNavLinkHandler={ sidebarNavLinkHandler }
              >
                <span className='nav-link-label'>{ link.caption }</span>
              </NavigationLink>
            </>
          );
        })
      }
      { loggedIn ?
        <Link className='customer-link-component' href={ signOutHref }>
          <FormattedMessage id='Shared:AccountTray:SignOut' />
        </Link>
        : <SignIn isReseller={ isReseller } loginLink={ loginLink } appEid={ appEid } />
      }
      { accountTrayBottomContent ? accountTrayContent : '' }
    </React.Fragment>
  );
}

AccountTrayNav.propTypes = {
  onClose: PropTypes.func,
  navigation: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]))),
  signOutHref: PropTypes.string,
  userName: PropTypes.string,
  sidebarNavLinkHandler: PropTypes.func,
  accountTrayBottomNav: PropTypes.node,
  appEid: PropTypes.func,
  loginLink: PropTypes.string,
  isReseller: PropTypes.bool
};

// eslint-disable-next-line complexity
export function AccountInfoContent(props) {
  const {
    userName,
    fullName,
    shopperId,
    isProShopper,
    proRewardPoints,
    vip,
    pcx,
    appEid,
    navigation,
    accountTrayBottomNav,
    onClose,
    sidebarNavLinkHandler,
    isPartners,
    loggedIn
  } = props;
  const { market,
    requestedHeader,
    privateLabelId,
    urls,
    features,
    privateLabelType
  } = useRootProps();

  const pageEid = appEid.extend('account_info');
  const { createAccountLink, loginLink, logoutLink } = useGetSsoLinks();
  const isInternalSidebar = requestedHeader === TYPES.internal;
  const isAfternic = [497036, 1001836].includes(privateLabelId);
  const isReseller = privateLabelId !== 1;
  const isBoutiqueBrand = privateLabelType === 14;
  const customerName = fullName || userName || '';

  const accountTrayNav = <AccountTrayNav
    onClose={ onClose }
    userName={ userName }
    navigation={ navigation }
    signOutHref={ logoutLink }
    loggedIn={ loggedIn }
    loginLink={ loginLink }
    appEid={ appEid }
    isReseller={ isReseller }
    sidebarNavLinkHandler={ sidebarNavLinkHandler }
    accountTrayBottomNav={ accountTrayBottomNav }
  />;

  // show GD logged out account-tray - matches the FOS account-tray
  const showGDLoggedOutAccountTray = !loggedIn && privateLabelId === 1;

  const loggedOutGDAccountTray =
    <>
      <SignIn isReseller={ false } loginLink={ loginLink } appEid={ appEid } />
      { isInternalSidebar ? null :
        <React.Fragment>
          <div className='account-info-divider' />
          <CreateAccount createAccountLink={ createAccountLink } appEid={ appEid } />
          <div className='account-info-divider' />
          <QuickLinks urls={ urls } appEid={ appEid } />
          <InboxLinks urls={ urls } { ...props } />
        </React.Fragment>
      }
    </>;

  // GoDaddy loggedIn OR non-GD(loggedIn or not)
  const accountTray =
    <>
      { loggedIn &&
        <div className='customer-info'>
          { !isAfternic && <div className='avatar'>
            <ProfileAvatar showAvatarImage={ isPartners } />
          </div> }
          { !isAfternic && <h5 className='customer-name'>{ customerName }</h5> }
          <div className='customer-details'>
            { isAfternic &&
            <div className='shopper-id'>
              <FormattedMessage id='PC:AppHeader:Header:UserName' />:{' '}
              <span>{ userName }</span>
            </div>
            }
            { !isAfternic && !isInternalSidebar &&
            <>
              <div className='shopper-id'>
                <FormattedMessage id='PC:AppHeader:Header:CustomerNumber' />:{' '}
                <span>{ shopperId }</span>
              </div>
              { !isBoutiqueBrand &&
                  <div className='shopper-pin'>
                    <FormattedMessage id='Shared:AccountTray:CustomerPinLabel' />:{' '}
                    <a
                      href={ urls.resetPin.href }
                      rel='noreferrer'
                      target='_blank'
                      data-eid={ pageEid('reset_pin.link.click') }
                    >
                      <FormattedMessage id='Shared:UtilityBar:AccountTray:ViewPin' />
                    </a>
                  </div>
              }
            </>
            }
            { isProShopper && isPartners && !pcx &&
            <div className='reward-points'>
              <FormattedMessage
                id='PC:AppHeader:Header:RewardPoints'
                values={{ points: <span>{proRewardPoints}</span> }}
              />
            </div>
            }
          </div>
        </div>
      }
      { typeof vip === 'object' && privateLabelId === 1 && vip &&
      <>
        <div className='account-info-divider' />
        <div className='shopper-vip account-info-section'>
          <div className='account-info-title'>
            <FormattedMessage id='Shared:AccountTray:Vip:Title' />
          </div>
          <div className='shopper-vip-details'>
            <FormattedMessage id='Shared:AccountTray:Vip:VipExecName' />:&nbsp;
            { `${vip.repname}` }
          </div>
          <div className='shopper-vip-details'>
            <FormattedMessage id='Shared:AccountTray:Vip:VipExecPhone' />:&nbsp;
            <a href={ `tel:${vip.phone}` }>
              { `${vip.phone}` }
            </a>
          </div>
          <div className='shopper-vip-details'>
            <FormattedMessage id='Shared:AccountTray:Vip:VipExecEmail' />:&nbsp;
            <a href={ `mailto:${vip.email}` }>
              { `${vip.email}` }
            </a>
          </div>
        </div>
      </>
      }

      {accountTrayNav}
    </>;

  return (
    <div className='account-info-content'>
      { showGDLoggedOutAccountTray ? loggedOutGDAccountTray : accountTray }
      {!isAfternic && !isInternalSidebar && <AccountInfoFooter
        urls={ urls }
        market={ market }
        features={ features }
        privateLabelId={ privateLabelId }
      />}
    </div>
  );
}

AccountInfoContent.propTypes = {
  userName: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  shopperId: PropTypes.string.isRequired,
  isProShopper: PropTypes.bool.isRequired,
  loggedIn: PropTypes.bool,
  proRewardPoints: PropTypes.number,
  vip: PropTypes.shape({
    repname: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string
  }),
  pcx: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  navigation: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]))),
  appEid: PropTypes.func,
  sidebarNavLinkHandler: PropTypes.func,
  urls: PropTypes.shape({}),
  accountTrayBottomNav: PropTypes.node,
  isPartners: PropTypes.bool
};

function AccountInfo({
  loggedIn,
  isOpen,
  onClose,
  appEid,
  navigation = [],
  sidebarNavLinkHandler,
  accountTrayBottomNav,
  isPartners
}) {
  const {
    privateLabelId
  } = useRootProps();

  const { userData, userDataLoaded } = useUserData();
  const ref = useRef(null);
  const isReseller = privateLabelId !== 1;

  useClickAway(ref, () => {
    if (!isModalOpen()) {
      onClose();
    }
  });

  let insideContent = null;

  // TODO: We should have the uxData right away and also in SSR, maybe we can optimize this so that it's SSRed
  if (userDataLoaded) {
    const { shopperData = {}, fullName = '', username = '', vip, pcx, loggedIn } = userData;
    const {
      shopperId = '',
      isPro: isProShopper = false,
      rewardPoints: proRewardPoints = 0
    } = shopperData;

    insideContent = (
      <AccountInfoContent
        userName={ username }
        fullName={ fullName }
        shopperId={ shopperId }
        isProShopper={ isProShopper }
        proRewardPoints={ proRewardPoints }
        vip={ vip }
        pcx={ pcx }
        appEid={ appEid }
        navigation={ navigation }
        accountTrayBottomNav={ accountTrayBottomNav }
        onClose={ onClose }
        sidebarNavLinkHandler={ sidebarNavLinkHandler }
        isPartners={ isPartners }
        loggedIn={ loggedIn }
      />
    );
  } else {
    insideContent = <Spinner className='center-spinner' />;
  }

  return (
    <div
      className={ classNames('account-info-component',
        {
          'account-info-open': isOpen,
          'reseller-info': isReseller,
          'logged-in': loggedIn
        }
      ) }
      ref={ ref }
    >
      { insideContent }
    </div>
  );
}

AccountInfo.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appEid: PropTypes.func,
  navigation: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]))),
  sidebarNavLinkHandler: PropTypes.func,
  accountTrayBottomNav: PropTypes.node,
  isPartners: PropTypes.bool,
  loggedIn: PropTypes.bool
};

export default AccountInfo;
